import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { images, auth } from '../utils/api';
import { useAuth } from './AuthContext';

const AvatarContext = createContext();

export function AvatarProvider({ children }) {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const { user, updateUser } = useAuth();

  // Update avatar URL when user changes
  useEffect(() => {
    if (!user || !user.avatar_url) {
      setAvatarUrl(null);
      return;
    }

    // Use images helper to get R2 URL
    setAvatarUrl(images.getImage(user.avatar_url));
  }, [user]);

  const updateAvatar = useCallback(async (file) => {
    try {
      await auth.uploadAvatar(file);
      await updateUser();
    } catch (error) {
      console.error('Failed to update avatar:', error);
      throw error;
    }
  }, [updateUser]);

  const deleteAvatar = useCallback(async () => {
    try {
      await auth.deleteAvatar();
      await updateUser();
    } catch (error) {
      console.error('Failed to delete avatar:', error);
      throw error;
    }
  }, [updateUser]);

  // Handle image loading error by triggering a reload
  const handleImageError = useCallback((e) => {
    const img = e.target;
    const retryCount = Number(img.dataset.retryCount || 0);
    
    if (retryCount < 3) { // Limit retries to prevent infinite loops
      img.dataset.retryCount = retryCount + 1;
      // Add timestamp to bypass cache
      img.src = `${avatarUrl}?retry=${Date.now()}`;
    }
  }, [avatarUrl]);

  return (
    <AvatarContext.Provider value={{ 
      avatarUrl,
      updateAvatar,
      deleteAvatar,
      handleImageError
    }}>
      {children}
    </AvatarContext.Provider>
  );
}

export function useAvatar() {
  const context = useContext(AvatarContext);
  if (context === undefined) {
    throw new Error('useAvatar must be used within an AvatarProvider');
  }
  return context;
}
